import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { vendorApi } from '../../services/api';

// ── Value validators (mirrors PartArticleForm) ───────────────────────────────
const isDashValue       = (v) => typeof v === 'string' && /^\s*[-–—]+\s*$/.test(v.trim());
const isSpecialCharsOnly = (v) => typeof v === 'string' && v.trim().length > 0 && /^[^a-zA-Z0-9]+$/.test(v.trim());
const isAlphabeticText  = (v) => /^[A-Za-z ]+$/.test(String(v ?? '').trim());
const isNumericDimension = (v) => /^\d+(\.\d{1,2})?$/.test(String(v ?? '').trim());
const isEditableDecimal = (v) => /^\d*(?:\.\d{0,2})?$/.test(v);

/** Returns specific error message for a field value, or null if OK */
const getValueError = (v) => {
  if (!v || !String(v).trim()) return 'required';
  if (isDashValue(String(v)))         return 'Dashes not allowed';
  if (isSpecialCharsOnly(String(v)))  return 'Special characters only not allowed';
  return null;
};

// ── Required BOM columns ─────────────────────────────────────────────────────
const REQUIRED_FIELDS = [
  { key: 'partName',               label: 'Part Name' },
  { key: 'partDescription',        label: 'Part Description' },
  { key: 'stockType',              label: 'Stock Type' },
  { key: 'partColor',              label: 'Part Color' },
  { key: 'measurementUnit',        label: 'Measurement Unit' },
  { key: 'length',                 label: 'Length (L)' },
  { key: 'width',                  label: 'Width (W)' },
  { key: 'height',                 label: 'Height (H)' },
  { key: 'weightUnit',             label: 'Weight Unit' },
  { key: 'partWeight',             label: 'Weight' },
  { key: 'category',               label: 'Category' },
  { key: 'vendorPartNumber',       label: 'Vendor Part Number' },
  { key: 'vendorFactoryName',      label: "Vendor's Factory Name" },
  { key: 'supplierName',           label: 'Supplier Name' },
  { key: 'supplierPartNumber',     label: 'Supplier Part Number' },
  { key: 'uom',                    label: 'UoM' },
  { key: 'totalQtyInFg',           label: 'Total Qty in FG' },
  { key: 'costPerUom',             label: 'Cost per UoM' },
];

const ALL_FIELDS = [...REQUIRED_FIELDS];

const UOM_OPTIONS        = ['EACH', 'SET', 'PCS', 'PAIR', 'BOX', 'ROLL', 'MTR', 'KG', 'LTR'];
const STOCK_TYPE_OPTIONS = ['STOCK', 'SPECIAL'];
const MEASUREMENT_UNIT_OPTIONS = ['INCHES', 'CENTIMETER'];
const normalizeMeasurementUnit = (value) => {
  const unit = String(value ?? '').trim().toUpperCase();
  if (unit === 'IN') return 'INCHES';
  if (unit === 'CM') return 'CENTIMETER';
  return unit;
};
const WEIGHT_UNIT_OPTIONS = [
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'g', label: 'Grams (g)' },
];

const CATEGORY_OPTIONS = [
  { group: 'Fabric & Cores', items: ['Casing', 'Full Cushion', 'Core', 'Toss Pillow', 'Back Pillow', 'Leather'] },
  { group: 'Wood', items: ['Drawers', 'Panels', 'Full Wood Chair', 'Wood Legs', 'Console & Lid'] },
  { group: 'Electronics', items: [
    'Motors/Actuator', 'Massage Motor', 'Switches', 'Hand Wand', 'Control Pad',
    'Wireless Charger', 'USB Charger', 'Transformer', 'Cup Holder', 'Control Box',
    'Remote Control', 'Kit - Control Box/Remote', 'Compressor', 'Airbag', 'Y-Cable',
    'Extension Cable', 'Power Cable', 'Other Cable', 'Power Outlet', 'Fuse', 'Socket',
    'Reading Light', 'Speakers', 'LED Light', 'Sensor Buttons', 'Battery', 'Other Electronics',
  ]},
  { group: 'Mechanisms', items: [
    'Mechanism Scissors', 'Sleeper - Queen', 'Sleeper - Full', 'Sleeper - Twin',
    'Mechanism Tubes', 'Mechanism Springs', 'Headrest Mechanism', 'Lumbar Mechanism',
    'Headrest/Lumbar Mechanism Kit', 'Mechanism Hardware', 'Mechanism Base', 'Other Mechanisms',
  ]},
  { group: 'Hardware', items: [
    'Pull Handle', 'Storage Hinge/Mech', 'Bracket', 'Side Glides', 'Underneath Glide',
    'Headrest Latch', 'Seat Springs', 'Soft Close Piston', 'Caster Wheel',
    'Drawer/Door Handle', 'Drawer/Door Knob', 'Rubber Coaster', 'Footrest Plastic/Metal Support',
    'Power Motion Connector', 'Stationary Connector', 'Tray Cover - Plastic',
    'Tray Cover with Wireless Charger', 'Electronic Hardware', 'Heating Pad',
    'Pole / Rod / Stand', 'Metal Sofa Legs', 'Plastic Sofa Legs', 'Other Hardware',
  ]},
];

// Flat list of all "Category > Type" values (matches backend ALL_CATEGORIES)
const ALL_CATEGORY_VALUES = CATEGORY_OPTIONS.flatMap(({ group, items }) =>
  items.map(item => `${group} > ${item}`)
);

// Maps Excel header text → internal key (case-insensitive)
const HEADER_MAP = {
  'fg request number':        '_fgRequestNumber',
  'fg number':                '_fgRequestNumber',
  'finished good number':     '_fgRequestNumber',
  'request number':           '_fgRequestNumber',
  'part name':                'partName',
  'part description':         'partDescription',
  'stock type':               'stockType',
  'part color':               'partColor',
  'color':                    'partColor',
  'measurement unit':         'measurementUnit',
  'measurement unit (in/cm)': 'measurementUnit',
  'measurement unit (inches/centimeter)': 'measurementUnit',
  'measurement':              'width',        // legacy single field → maps to width
  'width':                    'width',
  'width (w)':                'width',
  'w (width)':                'width',
  'w':                        'width',
  'length':                   'length',
  'length (l)':               'length',
  'l (length)':               'length',
  'l':                        'length',
  'height':                   'height',
  'height (h)':               'height',
  'h (height)':               'height',
  'h':                        'height',
  'weight':                   'partWeight',
  'part weight':              'partWeight',
  'weight unit':              'weightUnit',
  'unit (weight)':            'weightUnit',
  'weight unit (lb/oz/kg/g)': 'weightUnit',
  'category':                 'category',
  'vendor part number':       'vendorPartNumber',
  "vendor's factory name":    'vendorFactoryName',
  'vendor factory name':      'vendorFactoryName',
  'supplier name':            'supplierName',
  'supplier part number':     'supplierPartNumber',
  'uom':                      'uom',
  'unit of measure':          'uom',
  'total qty in fg':          'totalQtyInFg',
  'qty in fg':                'totalQtyInFg',
  'cost per uom':             'costPerUom',
  'cost / uom':               'costPerUom',
  'currency':                 'currency',
};

const normaliseHeader = (header) => String(header ?? '')
  .replace(/^\uFEFF/, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/\s*\*+\s*$/u, '')
  .trim();

function findHeaderRow(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false,
  });

  let bestMatch = { index: -1, count: 0 };
  rows.slice(0, 25).forEach((cells, index) => {
    const mappedFields = new Set(
      cells
        .map(normaliseHeader)
        .map(header => HEADER_MAP[header])
        .filter(Boolean)
    );

    if (mappedFields.size > bestMatch.count) {
      bestMatch = { index, count: mappedFields.size };
    }
  });

  return bestMatch.count >= 3 ? bestMatch.index : -1;
}

/** Returns a map of { fieldKey → errorMessage } for all invalid fields in a row */
function getRowErrors(row) {
  const errors = {};
  REQUIRED_FIELDS.forEach(f => {
    if (['width', 'length', 'height'].includes(f.key)) return; // handled separately
    const val = row[f.key];
    const err = getValueError(val == null ? '' : String(val));
    if (err) errors[f.key] = err;
  });
  if (row.measurementUnit && !MEASUREMENT_UNIT_OPTIONS.includes(normalizeMeasurementUnit(row.measurementUnit))) {
    errors.measurementUnit = 'Select INCHES or CENTIMETER';
  }
  if (row.partColor && !isAlphabeticText(row.partColor)) {
    errors.partColor = 'Letters only';
  }
  const weight = String(row.partWeight || '').trim();
  if (weight && !isNumericDimension(weight)) {
    errors.partWeight = 'Use up to 2 decimal places';
  }
  // Dimensions: all three are required and must be valid before this row can move.
  const dims = ['width', 'length', 'height'];
  dims.forEach(k => {
    const value = String(row[k] || '');
    if (!value.trim()) errors[k] = 'required';
    else if (!isNumericDimension(value)) errors[k] = 'Use up to 2 decimal places';
  });
  return errors;
}

function getMissingFields(row) {
  return Object.keys(getRowErrors(row));
}

// ── Download template pre-filled for the assignment ──────────────────────────
function downloadTemplate(fgNumber) {
  const headers = [...ALL_FIELDS.map(f => f.label)];

  const exampleRow = [
    'Seat Motor',
    'Seat Motor (Dummy Model X1)',
    'STOCK',
    'Red',
    'INCHES',
    '20',
    '30',
    '10',
    'kg',
    '500',
    'Electronics > USB Charger',
    'MTR-DM-101',
    'Shenzhen Factory A',
    'OmniMotion',
    'OM-882',
    'EACH',
    2,
    15.00,
    'USD',
  ];

  const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow]);

  // Bold + blue header row
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'DBEAFE' } },
    };
  }

  // Column widths
  ws['!cols'] = [
    ...ALL_FIELDS.map(f => ({ wch: Math.max(f.label.length + 4, 18) })),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BOM Template');
  XLSX.writeFile(wb, `part_articles_template${fgNumber ? `_${fgNumber}` : ''}.xlsx`);
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
//
// Props:
//   assignmentId  — numeric assignment ID (from URL :id param)
//   fgNumber      — e.g. "FG-2024-001"  (displayed + validated)
//   mainProduct   — { fgArticleId, collectionName, productDescription, articleGroup, genericStyle }
//   onImport(rows)— called to merge rows into the BOM form (no backend save)
//   onClose()     — dismiss the modal
//   onDraftSaved()— called after a successful save-as-draft (parent should refresh)
// ────────────────────────────────────────────────────────────────────────────
export default function PartArticleExcelUpload({
  assignmentId,
  fgNumber,
  mainProduct,
  onImport,
  onClose,
  onDraftSaved,
}) {
  const [tab, setTab]             = useState('valid');
  const [validRows, setValidRows] = useState([]);
  const [errorRows, setErrorRows] = useState([]);
  const [fileName, setFileName]   = useState('');
  const [dragging, setDragging]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const fileRef                   = useRef();

  // ── Parse uploaded Excel ────────────────────────────────────────────────
  const parseFile = useCallback((file) => {
    const fileNameLower = file.name?.toLowerCase() || '';
    if (!fileNameLower.endsWith('.xlsx') && !fileNameLower.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx or .xls) can be uploaded.');
      return;
    }

    setValidRows([]);
    setErrorRows([]);
    setTab('valid');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type: 'array' });
        // Prefer the 'BOM Components' sheet by name; fall back to the first sheet.
        // This prevents accidentally reading a hidden helper sheet (e.g. '_Lookups')
        // if it happens to be listed first in the workbook's sheet order.
        const sheetName = wb.SheetNames.find(n => n.trim().toLowerCase() === 'bom components') ?? wb.SheetNames[0];
        const ws  = wb.Sheets[sheetName];
        const headerRow = ws ? findHeaderRow(ws) : -1;

        if (headerRow === -1) {
          setFileName('');
          toast.error('Could not find BOM column headers. Use the downloaded template or include at least Part Name, Part Description, and Stock Type.');
          return;
        }
        // range: 1 — skip the title banner (row 1) and use the column-header row (row 2) as keys
        // Keep blank rows so `idx` continues to match the physical Excel row.
        // This lets us ignore only the yellow row-3 sample, without discarding
        // a real row 4 that happens to reuse the sample's part numbers.
        const raw = XLSX.utils.sheet_to_json(ws, {
          defval: '',
          range: headerRow,
          raw: false,
          blankrows: true,
        });

        if (raw.length === 0) {
          toast.error('The Excel file appears to be empty.');
          return;
        }

        const mismatchWarnings = [];

        const normalised = raw.map((rawRow, idx) => {
          const hasBomValue = Object.entries(rawRow).some(([header, value]) =>
            HEADER_MAP[normaliseHeader(header)] && String(value ?? '').trim()
          );
          if (!hasBomValue) return null;

          const row = {
            _key:             `xl-${Date.now()}-${idx}`,
            _excelRow:        headerRow + idx + 2,
            id:               null,
            currency:         'USD',
            measurementUnit:  'INCHES',
            weightUnit:       'lb',
            pendingPhotos:    [],
            _fgRequestNumber: fgNumber || '',
          };
          Object.entries(rawRow).forEach(([header, value]) => {
            // Strip asterisks added to required-field headers in the template (e.g. "Part Name *")
            const normHeader = normaliseHeader(header);
            const mapped = HEADER_MAP[normHeader];
            if (mapped) {
              const parsedValue = value !== null && value !== undefined ? String(value).trim() : '';
              row[mapped] = mapped === 'measurementUnit'
                ? normalizeMeasurementUnit(parsedValue)
                : mapped === 'weightUnit'
                  ? parsedValue.toLowerCase() || 'lb'
                  : parsedValue;
            }
          });

          // Ignore the yellow example only when it is in row 3 of the supplied
          // template. Do not discard matching vendor data entered on row 4+.
          if (
            headerRow === 1 &&
            idx === 0 &&
            row.partName          === 'Seat Motor' &&
            row.vendorPartNumber  === 'MTR-DM-101' &&
            row.supplierPartNumber === 'OM-882'
          ) {
            return null;
          }

          // Warn if the FG number in the row doesn't match the current assignment
          if (
            row._fgRequestNumber &&
            fgNumber &&
            row._fgRequestNumber.toUpperCase() !== fgNumber.toUpperCase()
          ) {
            mismatchWarnings.push(headerRow + idx + 2);
          }

          // Always stamp the current assignment's FG number (authoritative)
          row._fgRequestNumber = fgNumber || row._fgRequestNumber;

          return row;
        }).filter(Boolean); // remove null entries (skipped example row)

        if (normalised.length === 0) {
          setFileName('');
          toast.error('No BOM data rows found. Add component rows below the column headers; the yellow example row is ignored.');
          return;
        }

        if (mismatchWarnings.length > 0) {
          toast(`Row${mismatchWarnings.length > 1 ? 's' : ''} ${mismatchWarnings.slice(0, 3).join(', ')}${mismatchWarnings.length > 3 ? '…' : ''}: FG Request Number didn't match this assignment — overridden to ${fgNumber}`, {
            icon: '⚠️', duration: 5000,
          });
        }

        const valid = [];
        const error = [];
        normalised.forEach(row => {
          const errors  = getRowErrors(row);
          const missing = Object.keys(errors);
          if (missing.length === 0) valid.push(row);
          else error.push({ ...row, _missing: missing, _errors: errors });
        });

        setValidRows(valid);
        setErrorRows(error);
        setTab(error.length > 0 ? 'error' : 'valid');
      } catch (err) {
        console.error('Excel parse error', err);
        setFileName('');
        toast.error('Could not read the Excel file. Please use the provided template.');
      }
    };
    reader.onerror = () => {
      setFileName('');
      toast.error('Could not read the Excel file. Please try downloading it again.');
    };
    reader.readAsArrayBuffer(file);
  }, [fgNumber]);

  // ── Edit an error-row cell; auto-move to valid when complete ───────────
  const handleErrorCellChange = (key, field, value) => {
    setErrorRows(prev => {
      const updated = prev.map(r => {
        if (r._key !== key) return r;
        const newRow  = { ...r, [field]: value };
        const errors  = getRowErrors(newRow);
        const missing = Object.keys(errors);
        return { ...newRow, _missing: missing, _errors: errors };
      });

      const stillErrors = updated.filter(r => r._missing.length > 0);
      const nowValid    = updated.filter(r => r._missing.length === 0);

      if (nowValid.length > 0) {
        setValidRows(prev2 => [
          ...prev2,
          ...nowValid.map(({ _missing, ...rest }) => rest),
        ]);
        setTab('valid');
        if (stillErrors.length > 0) setTimeout(() => setTab('error'), 1200);
      }
      return stillErrors;
    });
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  // ── Save as Draft directly to the backend ──────────────────────────────
  const handleSaveAsDraft = async () => {
    if (!assignmentId || validRows.length === 0) return;
    setSaving(true);
    try {
      const payload = {
        assignmentId,
        mainProduct: mainProduct || {},
        components: validRows.map(r => ({
          id:                     r.id || null,
          partName:               r.partName,
          partDescription:        r.partDescription,
          stockType:              r.stockType || 'STOCK',
          partColor:              r.partColor,
          length:                 r.length,
          width:                  r.width,
          height:                 r.height,
          measurementUnit:        r.measurementUnit,
          weightUnit:             r.weightUnit,
          partWeight:             r.partWeight,
          category:               r.category,
          vendorPartNumber:       r.vendorPartNumber,
          vendorFactoryName:      r.vendorFactoryName,
          supplierName:           r.supplierName,
          supplierPartNumber:     r.supplierPartNumber,
          uom:                    r.uom,
          totalQtyInFg:           r.totalQtyInFg !== '' ? Number(r.totalQtyInFg) : null,
          costPerUom:             r.costPerUom   !== '' ? Number(r.costPerUom)   : null,
          currency:               r.currency || 'USD',
        })),
      };
      await vendorApi.saveAsDraft(assignmentId, payload);
      toast.success(`${validRows.length} row${validRows.length !== 1 ? 's' : ''} saved as draft for assignment ${fgNumber || `#${assignmentId}`}`);
      onDraftSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasData    = validRows.length > 0 || errorRows.length > 0;
  const canImport  = validRows.length > 0 && errorRows.length === 0;

  const handleDiscardErrors = () => {
    setErrorRows([]);
    if (validRows.length > 0) setTab('valid');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: '96vw', maxWidth: 1280, maxHeight: '92vh' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Upload Excel — BOM Components</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-gray-400">
                All imported rows will be saved to this assignment
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 mt-1">
            <button
              onClick={() => vendorApi.downloadPartArticleTemplate().catch(() => toast.error('Could not download the template. Please try again.'))}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
              ⬇ Download Template
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2">✕</button>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {!hasData ? (
            <DropZone
              dragging={dragging}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            />
          ) : (
            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
              <span className="text-blue-600 font-medium">📊 {fileName}</span>
              <span className="text-gray-400 text-xs">·</span>
              <span className="text-gray-500 text-xs">{validRows.length + errorRows.length} rows parsed</span>
              <button
                onClick={() => fileRef.current?.click()}
                className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium underline">
                Replace file
              </button>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={e => {
              const f = e.target.files[0];
              if (f) { parseFile(f); e.target.value = ''; }
            }}
          />

          {hasData && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Tab bar */}
              <div className="flex border-b border-gray-200">
                <TabBtn active={tab === 'valid'} onClick={() => setTab('valid')}
                  count={validRows.length} color="green" label="✅ Valid Data" />
                <TabBtn active={tab === 'error'} onClick={() => setTab('error')}
                  count={errorRows.length} color="red"   label="⚠ Errors" />
              </div>

              {/* Valid tab */}
              {tab === 'valid' && (
                <div className="overflow-x-auto mt-3">
                  {validRows.length === 0
                    ? <EmptyState icon="✅" title="No valid rows yet"
                        sub={errorRows.length > 0
                          ? 'Fix the errors in the Errors tab — completed rows move here automatically.'
                          : 'Upload an Excel file to get started.'} />
                    : <DataTable rows={validRows} />
                  }
                </div>
              )}

              {/* Error tab */}
              {tab === 'error' && (
                <div className="overflow-x-auto mt-3">
                  {errorRows.length === 0
                    ? <EmptyState icon="🎉" title="No errors!" sub="All rows are valid and ready to import." />
                    : <>
                        <div className="mb-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
                          <span className="text-base">⚠️</span>
                          <span>
                            Cells highlighted in <span className="font-semibold text-red-600">red</span> are missing or invalid.
                            Fill them in — once a row is complete it moves to the <strong>Valid Data</strong> tab automatically.
                          </span>
                        </div>
                        <ErrorTable rows={errorRows} onChange={handleErrorCellChange} />
                      </>
                  }
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <div className="text-xs text-gray-500">
            {hasData
              ? <>
                  <span className="font-semibold text-green-700">{validRows.length}</span> row{validRows.length !== 1 ? 's' : ''} ready
                  {errorRows.length > 0 && <>, <span className="font-semibold text-red-600">{errorRows.length}</span> with errors — fix or discard errors to continue</>}
                  {errorRows.length === 0 && <>{' — '}will be assigned to <span className="font-mono font-semibold text-amber-700">{fgNumber || `Assignment #${assignmentId}`}</span></>}
                </>
              : 'Upload an Excel file to get started'}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm rounded-lg font-medium hover:bg-white transition-colors">
              Cancel
            </button>

            {/* Discard error rows — only shown when errors exist */}
            {errorRows.length > 0 && (
              <button
                onClick={handleDiscardErrors}
                className="flex items-center gap-1.5 px-5 py-2.5 border border-red-300 text-red-600 bg-white text-sm rounded-lg font-semibold hover:bg-red-50 transition-colors">
                🗑 Discard {errorRows.length} Error{errorRows.length !== 1 ? 's' : ''}
              </button>
            )}

            {/* Import to BOM form — disabled until all errors are resolved or discarded */}
            <button
              onClick={() => onImport(validRows)}
              disabled={!canImport}
              className="flex items-center gap-2 px-5 py-2.5 border border-blue-500 text-blue-600 bg-white text-sm rounded-lg font-semibold hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              ＋ Add to BOM Form
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}

// ── Drop Zone ────────────────────────────────────────────────────────────────
function DropZone({ dragging, onDragOver, onDragLeave, onDrop, onClick }) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onClick}
      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl cursor-pointer transition-all select-none py-16
        ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'}`}>
      <div className="text-5xl mb-4">📊</div>
      <p className="text-base font-semibold text-gray-700">Drag & drop your Excel file here</p>
      <p className="text-sm text-gray-400 mt-1">or <span className="text-blue-600 underline">click to browse</span></p>
      <p className="text-xs text-gray-300 mt-3">Supports .xlsx and .xls files</p>
    </div>
  );
}

// ── Tab Button ───────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, label, count, color }) {
  const colors = {
    green: { active: 'border-green-600 text-green-700', badge: 'bg-green-100 text-green-700' },
    red:   { active: 'border-red-500 text-red-600',     badge: 'bg-red-100 text-red-600' },
  };
  const c = colors[color] || colors.green;
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors
        ${active
          ? `${c.active} bg-white`
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
      {label}
      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${active ? c.badge : 'bg-gray-100 text-gray-500'}`}>
        {count}
      </span>
    </button>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ icon, title, sub }) {
  return (
    <div className="py-16 flex flex-col items-center justify-center text-center">
      <div className="text-4xl mb-3">{icon}</div>
      <p className="text-sm font-semibold text-gray-600">{title}</p>
      <p className="text-xs text-gray-400 mt-1 max-w-xs">{sub}</p>
    </div>
  );
}

// ── Valid Data Table (read-only) ─────────────────────────────────────────────
function DataTable({ rows }) {
  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="bg-green-50 border-b-2 border-green-200">
          <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Excel Row</th>
          {ALL_FIELDS.map(f => (
            <th key={f.key} className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              {f.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row, idx) => (
          <tr key={row._key} className="hover:bg-green-50/40 transition-colors">
            <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
            {ALL_FIELDS.map(f => (
              <td key={f.key} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                {row[f.key] || <span className="text-gray-300">—</span>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Error Table (editable) ───────────────────────────────────────────────────
function ErrorTable({ rows, onChange }) {
  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="bg-red-50 border-b-2 border-red-200">
          <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Excel Row</th>
          {ALL_FIELDS.map(f => (
            <th key={f.key} className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              {f.label}
              {REQUIRED_FIELDS.find(r => r.key === f.key) && <span className="text-red-500 ml-0.5">*</span>}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row, idx) => (
          <tr key={row._key} className="align-top hover:bg-red-50/30 transition-colors">
            <td className="px-3 py-2 text-gray-400 pt-3">{row._excelRow ?? idx + 1}</td>
            {ALL_FIELDS.map(f => {
              const errorMsg = row._errors?.[f.key] || null;
              return (
                <td key={f.key} className="px-2 py-1.5">
                  <ErrorCell fieldKey={f.key} value={row[f.key] ?? ''} errorMsg={errorMsg}
                    onChange={val => onChange(row._key, f.key, val)} />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Error Cell ───────────────────────────────────────────────────────────────
function ErrorCell({ fieldKey, value, errorMsg, onChange }) {
  const [local, setLocal] = useState(value);

  const base     = 'w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500';
  const errStyle = 'border-red-400 bg-red-50 text-red-700 placeholder-red-300';
  const okStyle  = 'border-gray-300 bg-white text-gray-700';

  const isRequired = errorMsg === 'required';
  const placeholder = isRequired ? '⚠ Required' : '';
  const inputClass  = `${base} ${errorMsg ? errStyle : okStyle}`;

  const InlineError = () => errorMsg && !isRequired
    ? <p className="mt-0.5 text-xs text-red-600 font-medium leading-tight">{errorMsg}</p>
    : null;

  // Dropdowns
  if (fieldKey === 'category') {
    return (
      <div>
        <select value={value} onChange={e => onChange(e.target.value)}
          className={`${inputClass}`} style={{ minWidth: 200 }}>
          <option value="">{isRequired ? '⚠ Required' : 'Select category…'}</option>
          {CATEGORY_OPTIONS.map(({ group, items }) => (
            <optgroup key={group} label={group}>
              {items.map(item => (
                <option key={item} value={`${group} > ${item}`}>{item}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'stockType') {
    return (
      <div>
        <select value={value} onChange={e => onChange(e.target.value)}
          className={inputClass}>
          <option value="">{isRequired ? '⚠ Required' : 'Select…'}</option>
          {STOCK_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o.charAt(0) + o.slice(1).toLowerCase()}</option>)}
        </select>
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'measurementUnit') {
    return (
      <div>
        <select value={normalizeMeasurementUnit(value)} onChange={e => onChange(e.target.value)}
          className={inputClass}>
          <option value="">{isRequired ? 'Required' : 'Select unit...'}</option>
          {MEASUREMENT_UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
        </select>
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'weightUnit') {
    return (
      <div>
        <select value={String(value).toLowerCase()} onChange={e => onChange(e.target.value)}
          className={inputClass}>
          <option value="">{isRequired ? 'Required' : 'Select unit...'}</option>
          {WEIGHT_UNIT_OPTIONS.map(unit => (
            <option key={unit.value} value={unit.value}>{unit.label}</option>
          ))}
        </select>
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'uom') {
    return (
      <div>
        <select value={value} onChange={e => onChange(e.target.value)}
          className={inputClass}>
          <option value="">{isRequired ? '⚠ Required' : 'Select…'}</option>
          {UOM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <InlineError />
      </div>
    );
  }

  // Number inputs
  if (fieldKey === 'totalQtyInFg') {
    return (
      <div>
        <input type="number" min="1" value={local}
          onChange={e => setLocal(e.target.value)}
          onBlur={e => onChange(e.target.value)}
          placeholder={placeholder} style={{ minWidth: 80 }}
          className={inputClass} />
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'costPerUom') {
    return (
      <div>
        <input type="number" min="0" step="0.01" value={local}
          onChange={e => setLocal(e.target.value)}
          onBlur={e => onChange(e.target.value)}
          placeholder={isRequired ? '⚠ Required' : '0.00'} style={{ minWidth: 80 }}
          className={inputClass} />
        <InlineError />
      </div>
    );
  }
  if (['length', 'width', 'height', 'partWeight'].includes(fieldKey)) {
    return (
      <div>
        <input type="text" inputMode="decimal" value={local}
          onChange={e => {
            const nextValue = e.target.value;
            // Imported values may already be invalid (for example, "kjsdf").
            // Let the user delete or replace that value; otherwise the numeric
            // input filter would prevent every backspace from taking effect.
            if (!isEditableDecimal(String(local)) || isEditableDecimal(nextValue)) {
              setLocal(nextValue);
              onChange(nextValue);
            }
          }}
          onBlur={e => onChange(e.target.value)}
          placeholder={isRequired ? 'Required' : '0.00'} style={{ minWidth: 80 }}
          className={inputClass} />
        <InlineError />
      </div>
    );
  }
  if (fieldKey === 'partColor') {
    return (
      <div>
        <input type="text" value={local}
          onChange={e => {
            const nextValue = e.target.value;
            if (/^[a-zA-Z ]*$/.test(nextValue)) {
              setLocal(nextValue);
              onChange(nextValue);
            }
          }}
          onBlur={e => onChange(e.target.value)}
          placeholder={placeholder} style={{ minWidth: 110 }}
          className={inputClass} />
        <InlineError />
      </div>
    );
  }

  // Text inputs
  return (
    <div>
      <input type="text" value={local}
        onChange={e => { setLocal(e.target.value); onChange(e.target.value); }}
        onBlur={e => onChange(e.target.value)}
        placeholder={placeholder} style={{ minWidth: 110 }}
        className={inputClass} />
      <InlineError />
    </div>
  );
}
